<template>
  <OnBoardingFrame
    back
    :step="4"
    :title="heading"
    :subtitle="$t('on_boarding.profile_country_page.subtitle')"
    :error="errorMessage"
    @back="backTapped"
  >
    <!-- Step 4. Mirrors the mobile Country screen (iOS OnBoardingCountryViewController /
         Android OnboardingCountryFragment), including the "Hey <name>," greeting.
         The list is fetched once and filtered in the browser — it's ~350 rows, so a
         round-trip per keystroke would be slower and no more accurate. -->
    <div class="ob-subblock">
      <label v-if="!countriesFailed" class="ob-search">
        <span class="ms" aria-hidden="true">search</span>
        <input
          id="countrySearch"
          ref="searchInput"
          v-model="query"
          type="search"
          :placeholder="$t('on_boarding.profile_country_page.search_placeholder')"
          :aria-label="$t('on_boarding.profile_country_page.placeholder')"
          autocomplete="country-name"
          role="combobox"
          aria-controls="countryResults"
          :aria-expanded="visibleCountries.length > 0"
        >
      </label>

      <p v-if="isLoadingCountries" class="ob-status country-status">
        {{ $t('on_boarding.profile_country_page.loading') }}
      </p>
      <!-- A failed fetch is called out separately from "no match": an empty list
           looks identical to a bad search, and without a way forward the user is
           stuck on a step whose Continue can never enable. -->
      <div v-else-if="countriesFailed" class="ob-status country-status">
        <p>{{ $t('on_boarding.profile_country_page.load_failed') }}</p>
        <button type="button" class="ob-btn ob-btn-secondary ob-btn-md ob-btn-auto" @click="retry">
          <span class="ms" aria-hidden="true">refresh</span>
          <span>{{ $t('on_boarding.profile_country_page.retry') }}</span>
        </button>
      </div>
      <p v-else-if="visibleCountries.length === 0" class="ob-status country-status">
        {{ $t('on_boarding.profile_country_page.no_results') }}
      </p>

      <ul v-else id="countryResults" class="ob-options country-list" role="listbox">
        <li v-for="country in visibleCountries" :key="country.id">
          <button
            type="button"
            class="ob-option country-option"
            :class="{ 'country-option--selected': String(country.id) === String(selectedCountryId) }"
            role="option"
            :aria-selected="String(country.id) === String(selectedCountryId)"
            @click="select(country)"
          >
            <span>{{ country.name }}</span>
            <span v-if="String(country.id) === String(selectedCountryId)" class="ms" aria-hidden="true">check</span>
          </button>
        </li>
      </ul>
    </div>

    <template #footer>
      <button type="button" class="ob-btn ob-btn-primary" :disabled="!canSubmit" @click="submit">
        {{ continueLabel }}
      </button>
    </template>
  </OnBoardingFrame>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingFrame from './OnBoardingFrame.vue';
import { filterCountries, type SignupCountry } from '../../../utils/helpers/signupProfile';

// Enough to scroll through without rendering all ~350 rows on every keystroke.
const MAX_VISIBLE = 60;

export default defineComponent({
  name: 'OnBoardingProfileCountryPage',
  components: { OnBoardingFrame },
  data() {
    return {
      query: '',
      selectedCountryId: this.$store.state.onBoarding.profileCountryId as string | number | null,
    };
  },
  computed: {
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    isLoadingCountries(): boolean {
      return this.$store.state.onBoarding.countriesLoading;
    },
    countries(): SignupCountry[] {
      return this.$store.state.onBoarding.countries;
    },
    countriesFailed(): boolean {
      return this.$store.state.onBoarding.countriesFailed;
    },
    continueLabel(): string {
      return this.countriesFailed
        ? this.$t('on_boarding.profile_country_page.continue_without')
        : this.$t('on_boarding.profile_country_page.continue');
    },
    firstName(): string {
      return this.$store.state.onBoarding.profileFirstName;
    },
    // Falls back to the name-less wording rather than rendering "Hey ,".
    heading(): string {
      return this.firstName
        ? this.$t('on_boarding.profile_country_page.title', { name: this.firstName })
        : this.$t('on_boarding.profile_country_page.title_no_name');
    },
    visibleCountries(): SignupCountry[] {
      return filterCountries(this.countries, this.query).slice(0, MAX_VISIBLE);
    },
    canSubmit(): boolean {
      // When the list couldn't load there is nothing to pick, so the step must
      // still be passable — the country is simply left unset.
      return this.selectedCountryId != null || this.countriesFailed;
    },
  },
  mounted() {
    // The list may still be in flight when the step opens; the action is a
    // no-op once it has loaded.
    this.$store.dispatch('onBoarding/fetchCountries');
    (this.$refs.searchInput as HTMLInputElement | undefined)?.focus();
  },
  methods: {
    select(country: SignupCountry) {
      this.selectedCountryId = country.id;
      this.$store.commit('onBoarding/setError', null);
    },
    retry() {
      this.$store.dispatch('onBoarding/retryFetchCountries');
    },
    submit() {
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.$t('on_boarding.profile_country_page.error_missing'));
        return;
      }
      this.$store.dispatch('onBoarding/submitProfileCountry', this.selectedCountryId);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>
